/** The table every chart ships beside itself (ADR-0090's first rule).
 *
 * **The table is what a screen reader reads and the chart is `aria-hidden`.** Both are written from
 * the same data by the same component, so they cannot disagree, and a chart without this is
 * unfinished. It is visually hidden rather than hidden: `display: none` would remove it from the
 * accessibility tree as well as the page, which is the one thing it is for.
 *
 * It holds text only. A control inside a visually-hidden table is a Tab stop nobody can see, so the
 * keyboard route to the same figures is the buttons the screen already has (the KPI strip and the
 * scope cells), and the marks are pointer targets onto the same breakdown drawer.
 *
 * **`visible`** (ADR-0212, P7) is the chart card's Table button: the same table, drawn for everyone
 * in place of the picture. It is the same element with the same data, only no longer hidden.
 */

import styles from "./ChartTable.module.css";

export function ChartTable({
  caption,
  head,
  rows,
  note,
  visible = false,
}: {
  caption: string;
  head: readonly string[];
  rows: readonly (readonly string[])[];
  /** What the chart leaves out, said where a reader of the table will meet it. */
  note?: string;
  /** Draw it for everyone (the card's Table view) rather than for assistive technology only. */
  visible?: boolean;
}) {
  return (
    <div className={visible ? styles.visible : "visuallyHidden"} data-testid="chart-table">
      <table className={visible ? styles.table : undefined}>
        <caption className={visible ? "visuallyHidden" : undefined}>{caption}</caption>
        <thead>
          <tr>
            {head.map((cell) => (
              <th key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row[0] ?? ""}-${index}`}>
              {row.map((cell, column) => (
                <td key={column}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {note ? <p className={visible ? styles.note : undefined}>{note}</p> : null}
    </div>
  );
}
